import { Link } from "react-router-dom";
import { Wrench, Stethoscope, Scissors, Home as HomeIcon, X, Check, ArrowUpRight } from "lucide-react";
import { useEffect, useState } from "react";
import api from "@/lib/api";
import PublicChatWidget from "@/components/PublicChatWidget";
import {
  T, FONT_DISPLAY, FONT_MONO, PageShell, TopNav, Footer,
  container, eyebrow, h2, italicAccent,
} from "@/lib/hybridTheme";

const INDUSTRIES = [
  {
    id: "contractor", icon: Wrench,
    title: "Contractors & home services",
    lead: "Plumbers, electricians, HVAC, landscaping — if the job happens on-site, the phone doesn't stop ringing just because your hands are busy.",
    problems: [
      "Jobsite noise means missed calls become missed jobs",
      "Emergency calls at night go straight to voicemail",
      "Quote requests pile up and go cold before you call back",
      "Double-booked jobs from manual scheduling mistakes",
    ],
    solutions: [
      "Answers every call instantly, even mid-job, and books straight to your calendar",
      "Flags true emergencies and texts you immediately instead of waiting for a callback",
      "Texts quote requests same-day so leads don't go to the next contractor",
      "Sends arrival-window reminders so customers aren't left guessing",
    ],
  },
  {
    id: "dental", icon: Stethoscope,
    title: "Dental & medical practices",
    lead: "Your front desk is already busy checking patients in. New patient calls and reschedules shouldn't have to wait for an opening.",
    problems: [
      "Patients calling mid-appointment get put on hold or call back later",
      "No-shows cost a full appointment slot with no warning",
      "Reschedule requests get lost in sticky notes and voicemail",
      "After-hours calls about pain or urgent issues go unanswered",
    ],
    solutions: [
      "Answers and books new patient calls without pulling staff off the front desk",
      "Sends automatic appointment reminders to cut no-shows before they happen",
      "Handles reschedules directly into the calendar, no manual re-entry",
      "Flags urgent after-hours calls so the right person gets notified right away",
    ],
  },
  {
    id: "salon", icon: Scissors,
    title: "Salons & spas",
    lead: "Every stylist's hands are full of a client's hair, not a phone. Bookings shouldn't depend on someone being free to pick up.",
    problems: [
      "Calls go unanswered while every chair is occupied",
      "Last-minute cancellations leave empty slots with no backfill",
      "New clients calling for the first time hang up if no one answers",
      "Rebooking the next appointment gets forgotten after checkout",
    ],
    solutions: [
      "Books appointments directly while your team stays with their current client",
      "Sends instant text alerts on cancellations so you can offer the slot to a waitlist",
      "Answers first-time callers professionally, every single time",
      "Automated rebooking reminders keep your chairs full week after week",
    ],
  },
  {
    id: "other", icon: HomeIcon,
    title: "Other local services",
    lead: "Cleaning, pest control, lawn care, auto repair — if your business runs on inbound calls and scheduled visits, this applies to you too.",
    problems: [
      "Seasonal call spikes overwhelm a small team",
      "Recurring service customers calling to reschedule clog up your day",
      "Price-shopping callers want fast answers you can't always give live",
      "No consistent way to ask happy customers for a review",
    ],
    solutions: [
      "Scales with call volume automatically — no hiring for busy season",
      "Handles routine reschedules without taking your time",
      "Answers common pricing questions instantly and accurately",
      "Sends review requests right after the job, while it's still fresh",
    ],
  },
];

export default function Industries() {
  const [agent, setAgent] = useState(null);
  useEffect(() => {
    api.get("/public/site-config").then(({ data }) => setAgent(data.agent || null)).catch(() => {});
  }, []);
  useEffect(() => {
    if (window.location.hash) {
      const el = document.querySelector(window.location.hash);
      if (el) setTimeout(() => el.scrollIntoView({ behavior: "smooth", block: "start" }), 200);
    }
  }, []);

  return (
    <PageShell testId="industries-page">
      <TopNav activeKey="industries" />

      <section style={{ padding: "112px 0 56px", position: "relative", overflow: "hidden" }}>
        <div style={{ position: "absolute", top: -100, right: -100, width: 600, height: 600, background: "radial-gradient(circle, rgba(229,83,59,0.08) 0%, transparent 60%)", pointerEvents: "none", filter: "blur(50px)" }} />
        <div style={container()}>
          <div style={{ maxWidth: 880, position: "relative" }}>
            <div style={eyebrow()}>Built for the trades</div>
            <h1 style={{ fontFamily: FONT_DISPLAY, fontSize: "clamp(52px, 6.5vw, 96px)", lineHeight: 0.98, letterSpacing: "-0.035em", fontWeight: 400, margin: "16px 0 24px", color: T.ink }}>
              Tuned <em style={italicAccent(T.brass)}>per industry</em> —<br />
              not one-size-fits-all.
            </h1>
            <p style={{ fontSize: 20, lineHeight: 1.55, color: T.inkMuted, maxWidth: 720, margin: 0 }}>
              Same AI concierge, sharpened for the calls your specific business gets. See the pain, then the fix.
            </p>
          </div>
        </div>
      </section>

      {INDUSTRIES.map((ind, idx) => {
        const isAlt = idx % 2 === 1;
        return (
          <section
            key={ind.id}
            id={ind.id}
            style={{
              padding: "88px 0",
              background: isAlt ? T.canvas : T.cream,
              borderTop: `1px solid ${T.lineSoft}`,
            }}
          >
            <div style={container()}>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 2fr", gap: 56, alignItems: "start" }}>
                <div>
                  <div style={{ display: "inline-flex", alignItems: "center", justifyContent: "center", width: 56, height: 56, background: T.ink, color: T.coral, marginBottom: 24 }}>
                    <ind.icon size={24} />
                  </div>
                  <div style={eyebrow()}>{String(idx + 1).padStart(2, "0")} · Industry</div>
                  <h2 style={{ fontFamily: FONT_DISPLAY, fontSize: 44, lineHeight: 1.05, letterSpacing: "-0.02em", fontWeight: 400, margin: "12px 0 16px", color: T.ink }}>
                    {ind.title}
                  </h2>
                  <p style={{ fontSize: 16, color: T.inkMuted, lineHeight: 1.65, margin: 0 }}>{ind.lead}</p>
                  <Link to="/#contact" style={{ marginTop: 24, background: T.coral, color: T.cream, border: "none", padding: "12px 20px", fontSize: 14, fontWeight: 700, textDecoration: "none", display: "inline-flex", alignItems: "center", gap: 8 }}>
                    Book a demo <ArrowUpRight size={14} />
                  </Link>
                </div>

                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 20 }}>
                  <div style={{ background: T.panel, border: `1px solid ${T.line}`, padding: "28px 24px" }}>
                    <div style={{ fontSize: 11, fontFamily: FONT_MONO, letterSpacing: "0.12em", textTransform: "uppercase", color: T.coralDark, fontWeight: 700 }}>Today</div>
                    <div style={{ fontFamily: FONT_DISPLAY, fontSize: 22, fontWeight: 500, fontStyle: "italic", color: T.ink, marginTop: 6 }}>Where money leaks</div>
                    <ul style={{ listStyle: "none", padding: 0, margin: "20px 0 0" }}>
                      {ind.problems.map((p, i) => (
                        <li key={i} style={{ display: "flex", gap: 10, alignItems: "flex-start", marginBottom: 12, fontSize: 14, color: T.inkSoft, lineHeight: 1.55 }}>
                          <X size={16} strokeWidth={2.5} style={{ marginTop: 2, color: T.coral, flexShrink: 0 }} />
                          <span>{p}</span>
                        </li>
                      ))}
                    </ul>
                  </div>

                  <div style={{ background: T.ink, color: T.cream, padding: "28px 24px" }}>
                    <div style={{ fontSize: 11, fontFamily: FONT_MONO, letterSpacing: "0.12em", textTransform: "uppercase", color: T.coralDark, fontWeight: 700 }}>With the AI</div>
                    <div style={{ fontFamily: FONT_DISPLAY, fontSize: 22, fontWeight: 500, fontStyle: "italic", marginTop: 6 }}>How the leak stops</div>
                    <ul style={{ listStyle: "none", padding: 0, margin: "20px 0 0" }}>
                      {ind.solutions.map((s, i) => (
                        <li key={i} style={{ display: "flex", gap: 10, alignItems: "flex-start", marginBottom: 12, fontSize: 14, lineHeight: 1.55, color: T.cream }}>
                          <Check size={16} strokeWidth={2.5} style={{ marginTop: 2, color: "#059669", flexShrink: 0 }} />
                          <span>{s}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              </div>
            </div>
          </section>
        );
      })}

      <section style={{ padding: "72px 0 120px" }}>
        <div style={container()}>
          <div style={{ background: T.ink, color: T.cream, padding: "56px 48px", textAlign: "center" }}>
            <h3 style={{ fontFamily: FONT_DISPLAY, fontSize: 48, fontWeight: 400, letterSpacing: "-0.03em", margin: 0 }}>
              Don&apos;t see your <em style={italicAccent(T.coral)}>trade?</em>
            </h3>
            <p style={{ marginTop: 16, opacity: 0.75, maxWidth: 620, margin: "16px auto 28px", lineHeight: 1.6 }}>
              If it runs on inbound calls, it fits. Book a 15-min call and we&apos;ll tune the agent to your services live.
            </p>
            <Link to="/#contact" style={{ background: T.coral, color: T.cream, border: "none", padding: "16px 28px", fontSize: 15, fontWeight: 700, textDecoration: "none", display: "inline-flex", alignItems: "center", gap: 8 }}>
              Book my demo <ArrowUpRight size={16} />
            </Link>
          </div>
        </div>
      </section>

      <Footer />
      <PublicChatWidget agent={agent} />
    </PageShell>
  );
}
